import { MagnifyingGlass } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { HashLoader } from "react-spinners";
import MerchantItem from "../../components/marketplace/MerchantItem";
import useMerchants, { MerchantListItem } from "../../hooks/useMerchants";

const Merchants = () => {
  const [page, setPage] = useState(1);
  const [merchantItems, setMerchantItems] = useState<MerchantListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { merchants, pagination, isLoading } = useMerchants({
    page,
    perPage: 50,
  });

  useEffect(() => {
    if (!merchants.length) {
      return;
    }
    setMerchantItems((currentMerchants) => {
      const existingIds = new Set(currentMerchants.map((merchant) => merchant.id));
      const nextMerchants = merchants.filter(
        (merchant) => !existingIds.has(merchant.id)
      );
      return [...currentMerchants, ...nextMerchants];
    });
  }, [merchants]);

  const filteredMerchants = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return merchantItems.filter((merchant) =>
      (merchant.business_name ?? "").toLowerCase().includes(q)
    );
  }, [merchantItems, searchTerm]);

  const canLoadMore = pagination
    ? pagination.current_page < pagination.last_page
    : merchants.length > 0;

  return (
    <div className="p-10 px-5 md:px-10 flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-medium">Merchants</h1>
        <div className="text-gray-500">
          Browse merchants selling on the Visaro marketplace.
        </div>
      </div>

      <div className="flex items-center gap-2 px-4 border border-gray-300 shadow rounded-lg focus-within:outline w-full max-w-md">
        <MagnifyingGlass size={20} />
        <input
          placeholder="Search merchants"
          name="search"
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="focus:outline-none py-3 text-sm w-full"
        />
      </div>

      {isLoading && page === 1 ? (
        <div className="py-20 flex items-center justify-center flex-col gap-5">
          <HashLoader size={30} />
          <div className="text-sm animate-pulse text-center">
            Loading Merchants
          </div>
        </div>
      ) : filteredMerchants.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-10">
          {filteredMerchants.map((merchant) => (
            <MerchantItem merchant={merchant} key={merchant.id} />
          ))}
        </div>
      ) : (
        <div className="py-20 text-center text-gray-500">
          No merchants found.
        </div>
      )}

      {canLoadMore && (
        <button
          type="button"
          disabled={isLoading}
          onClick={() => setPage((currentPage) => currentPage + 1)}
          className="mx-auto p-3 px-5 text-sm rounded-lg font-medium bg-visaro-primary-700 text-white disabled:opacity-60"
        >
          {isLoading ? "Loading..." : "Load more merchants"}
        </button>
      )}
    </div>
  );
};

export default Merchants;
